// teamService.js
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

class TeamService {
  // Helper method to get auth token from localStorage
  static getAuthToken() {
    return localStorage.getItem("authToken");
  }

  // Helper method to set auth token in localStorage
  static setAuthToken(token) {
    localStorage.setItem("authToken", token);
  }

  // Helper method to remove auth token from localStorage
  static removeAuthToken() {
    localStorage.removeItem("authToken");
  }

  // Helper method to get authenticated user from localStorage
  static getAuthUser() {
    const userStr = localStorage.getItem("authUser");
    return userStr ? JSON.parse(userStr) : null;
  }

  // Helper method to set authenticated user in localStorage
  static setAuthUser(user) {
    localStorage.setItem("authUser", JSON.stringify(user));
  }

  // Helper method to remove authenticated user from localStorage
  static removeAuthUser() {
    localStorage.removeItem("authUser");
  }

  // Register new team member
  static async register(data) {
    try {
      const response = await fetch(`${API_BASE_URL}/team/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
           'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Registration failed");
      }

      return result;
    } catch (error) {
      console.error("Registration error:", error);
      throw error;
    }
  }

  // Login team member
  static async login(email, password) {
    try {
      const response = await fetch(`${API_BASE_URL}/team/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
           'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ email, password }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Login failed");
      }

      // Store token and user data
      if (result.data.token) {
        this.setAuthToken(result.data.token);
        this.setAuthUser(result.data.user);
      }

      return result;
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  }

  // Forgot password - request reset link
  static async forgotPassword(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/team/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to send reset email");
      }

      return result;
    } catch (error) {
      console.error("Forgot password error:", error);
      throw error;
    }
  }

  // Verify reset token (called when user clicks email link)
  static async verifyResetToken(token) {
    try {
      const response = await fetch(`${API_BASE_URL}/team/verify-reset-token/${token}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Invalid or expired reset token");
      }

      return result;
    } catch (error) {
      console.error("Verify reset token error:", error);
      throw error;
    }
  }

  // Reset password using token (for non-authenticated users)
  static async resetPasswordWithToken(data) {
    try {
      const response = await fetch(`${API_BASE_URL}/team/reset-password-with-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(data), // { token, newPassword }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to reset password");
      }

      return result;
    } catch (error) {
      console.error("Reset password with token error:", error);
      throw error;
    }
  }

  // Change password for authenticated users (requires current password)
  static async changePassword(data) {
    try {
      const token = this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}/team/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data), // { currentPassword, newPassword }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to change password");
      }

      return result;
    } catch (error) {
      console.error("Change password error:", error);
      throw error;
    }
  }

  // Reset password for authenticated users (admin or self without current password)
  static async resetPassword(data) {
    try {
      const token = this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}/team/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data), // { newPassword }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to reset password");
      }

      return result;
    } catch (error) {
      console.error("Reset password error:", error);
      throw error;
    }
  }

  // Get all team members
  static async getAllTeamMembers() {
    try {
      const token = this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}/team`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to fetch team members");
      }

      return result;
    } catch (error) {
      console.error("Fetch team members error:", error);
      throw error;
    }
  }

  // Get authenticated user info
  static async getMe() {
    try {
      const token = this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}/team/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to fetch user info");
      }

      return result;
    } catch (error) {
      console.error("Fetch user info error:", error);
      throw error;
    }
  }

  // Update team member
  static async updateTeamMember(id, data) {
    try {
      const token = this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}/team/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to update team member");
      }

      return result;
    } catch (error) {
      console.error("Update team member error:", error);
      throw error;
    }
  }

  // Logout
  static async logout() {
    try {
      const token = this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}/team/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Logout failed");
      }

      // Clear local storage
      this.removeAuthToken();
      this.removeAuthUser();

      return result;
    } catch (error) {
      console.error("Logout error:", error);
      // Even if logout fails on server, clear local storage
      this.removeAuthToken();
      this.removeAuthUser();
      throw error;
    }
  }

  // Check if user is authenticated
  static isAuthenticated() {
    return !!this.getAuthToken();
  }

  // Get team members (alternative endpoint)
  static async getTeamMembers() {
    try {
      const token = this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}/team-members`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to fetch team members");
      }

      return result;
    } catch (error) {
      console.error("Fetch team members error:", error);
      throw error;
    }
  }

  // Get user with cache
  static async getMeWithCache() {
    try {
      // 1. Return cached user immediately if available
      const cachedUser = this.getAuthUser();
      if (cachedUser) {
        // 2. Refresh user in background (don't wait for it)
        this.getMe()
          .then(freshUser => {
            if (freshUser) {
              this.setAuthUser(freshUser.data); // Update cache with fresh data
            }
          })
          .catch(error => {
            console.error("Background user refresh failed:", error);
          });
        
        return { data: cachedUser }; // Return cached immediately
      }

      // 3. No cache available - make fresh request
      const freshResponse = await this.getMe();
      if (freshResponse.data) {
        this.setAuthUser(freshResponse.data); // Cache the fresh data
      }
      return freshResponse;
    } catch (error) {
      console.error("getMeWithCache error:", error);
      throw error;
    }
  }

  // Admin creates an invite for a new team member
  static async createInvite(data) {
    try {
      const token = this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}/invites`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data), // { email, role }
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || "Failed to create invite");
      }

      return result; // should include { inviteLink }
    } catch (error) {
      console.error("Create invite error:", error);
      throw error;
    }
  }

  // Validate invite & fetch details
  static async getInvite(token) {
    try {
      const response = await fetch(`${API_BASE_URL}/invites/${token}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || "Invalid or expired invite");
      }

      return result; // { email?, role }
    } catch (error) {
      console.error("Get invite error:", error);
      throw error;
    }
  }

  // Complete signup with invite
  static async signup(data) {
    try {
      const response = await fetch(`${API_BASE_URL}/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(data), // { name, email, password, invite }
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || "Signup failed");
      }

      return result;
    } catch (error) {
      console.error("Signup error:", error);
      throw error;
    }
  }

  // Delete a team member
  static async deleteTeamMember(id) {
    try {
      const token = this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}/team/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      // Try to parse JSON response, even if empty
      let result = {};
      try {
        result = await response.json();
      } catch {
        // Some DELETE endpoints return no body — that's fine
      }

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete team member");
      }

      return result;
    } catch (error) {
      console.error("Delete team member error:", error);
      throw error;
    }
  }

  // Get user roles (if needed)
  static async getUserRoles() {
    try {
      const token = this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}/team/roles`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to fetch user roles");
      }

      return result;
    } catch (error) {
      console.error("Fetch user roles error:", error);
      throw error;
    }
  }

  // Check token validity
  static async checkTokenValidity() {
    try {
      const token = this.getAuthToken();
      if (!token) {
        return { valid: false };
      }

      const response = await fetch(`${API_BASE_URL}/team/check-token`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      return { valid: response.ok, data: result };
    } catch (error) {
      console.error("Check token validity error:", error);
      return { valid: false };
    }
  }
  // Get all pending invites
static async getInvites() {
  try {
    const token = this.getAuthToken();

    const response = await fetch(`${API_BASE_URL}/invites`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.message || "Failed to fetch invites");
    }

    return result;
  } catch (error) {
    console.error("Fetch invites error:", error);
    throw error;
  }
}

// Resend an invite
static async resendInvite(inviteId) {
  try {
    const token = this.getAuthToken();

    const response = await fetch(`${API_BASE_URL}/invites/${inviteId}/resend`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.message || "Failed to resend invite");
    }

    return result;
  } catch (error) {
    console.error("Resend invite error:", error);
    throw error;
  }
}

// Cancel/Delete an invite
static async deleteInvite(inviteId) {
  try {
    const token = this.getAuthToken();

    const response = await fetch(`${API_BASE_URL}/invites/${inviteId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.message || "Failed to cancel invite");
    }

    return result;
  } catch (error) {
    console.error("Delete invite error:", error);
    throw error;
  }
}
  // Clear all authentication data (for forced logout after password reset)
static clearAllAuthData() {
  // Clear localStorage items
  localStorage.removeItem("authToken");
  localStorage.removeItem("authUser");
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("userId");
  localStorage.removeItem("userName");
  localStorage.removeItem("Role");
  localStorage.removeItem("userRole");
  
  // Clear sessionStorage
  sessionStorage.clear();
  
  // You might also want to clear cookies if you use them
  document.cookie.split(";").forEach(function(c) {
    document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
  });
}
}

export default TeamService;